import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {QuestionOptions} from '../../../constants/question-options';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {CustomValidators} from '../../../validator/custom-validator';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {Proposal} from 'src/app/model/proposal/proposal';
import {RiskLocation} from '../../../model/proposal/risk-location';
import {BinaryData} from '../../../model/proposal/binary-data';
import {UploadLocation} from '../../../enum/upload-location';
import {UploadComponent} from '../../upload-field/upload.component';
import {debounceTime, distinctUntilChanged, ReplaySubject, Subject, take, takeUntil} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {RiskLocationService} from '../../../services/proposal-service/risk-location.service';
import {Store} from '@ngrx/store';
import {User} from '../../../model/user';
import {selectUser} from '../../../store/user/selectors/user.selectors';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {SkendataRegistrationDialogComponent} from '../../dialogs/skendata-registration-dialog/skendata-registration-dialog.component';
import {SkendataService} from '../../../services/skendata-service/skendata.service';

@Component({
    selector: 'app-risk-location-form',
    templateUrl: './risk-location-form.component.html',
    styleUrls: ['../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: RiskLocationFormComponent }],
    standalone: false
})
export class RiskLocationFormComponent extends ProposalFormBase implements OnInit, AfterViewInit {

  @Input() proposalPreview: Proposal;

  @Output() riskLocationCompleted = new EventEmitter<boolean>();

  @ViewChild(UploadComponent) uploadComponent: UploadComponent;
  @ViewChild('fileInput') fileInput: ElementRef;

  uploadFile() {
    this.fileInput.nativeElement.click();
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.uploadComponent.submitFileUpload(input.files);
    }
  }

  QuestionOptions = QuestionOptions;

  riskLocationSearchArray: RiskLocationSearch[] = [];
  protected destroy$ = new Subject<void>();

  countryOptions: string[] = [];
  countryCodeOptions: string[] = QuestionOptions.countryCodes;

  formGroup = this.fb.group({
    riskLocations: this.fb.array<FormGroup<RiskLocationForm>>([]),
    riskLocationFiles: new FormControl<BinaryData[]>([]),
  });

  maxNumber = 5;
  UploadLocation = UploadLocation;
  skenDataCredentialsValid: boolean = true;
  enableSkenDataButton: boolean[] = []
  showSkenDataForm: boolean[] = [] // array of booleans for skendata info for every separate risk location
  user: User = null;

  constructor(private readonly fb: FormBuilder,
              private readonly translate: TranslateService,
              private readonly riskLocationService: RiskLocationService,
              private readonly userStore: Store<User>,
              private readonly dialog: MatDialog,
              private readonly skendataService: SkendataService) {
    super();
  }

  ngOnInit(): void {
    QuestionOptions.countryCodes.forEach(code =>
      this.countryOptions.push(this.translate.instant('countryCodes.' + code))
    );

    if (this.formGroup.controls.riskLocations.length === 0) {
      this.addForm();
    }

    this.userStore.select(selectUser).subscribe((user) => {
      this.user = user;
      if (user?.skenDataAccountId) {
        this.skenDataCredentialsValid = true;
      }
    })
  }

  updateProposal(proposal: Proposal): void {
    proposal.riskLocations = [];
    if (this.formGroup.value.riskLocations?.[0]?.description) {
      (this.formGroup.value.riskLocations ?? []).forEach(riskLocation =>
        proposal.riskLocations.push(new RiskLocation(riskLocation)));
    }
    (this.formGroup.value.riskLocationFiles ?? []).forEach(file => {
      file.uploadLocation = UploadLocation.RISKLOCATION;
      proposal.proposalInformation.attachedFiles.push(file);
    });
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.riskLocations?.length != null && proposal.riskLocations.length > 0) {

      for (let i = 0; i < proposal.riskLocations.length; i++) {
        this.formGroup.controls.riskLocations.at(i).patchValue(proposal.riskLocations[i]);
        if (proposal.proposalInformation.attachedFiles != null) {
          this.uploadComponent.addPreUploadedFiles(
            proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.RISKLOCATION)
          );
        }

        if (i + 1 < proposal.riskLocations.length) {
          this.addForm();
        }
      }
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if (proposal.riskLocations?.length != null && proposal.riskLocations.length > 0) {

      for (let i = 0; i < proposal.riskLocations.length; i++) {
        this.formGroup.controls.riskLocations.at(i).patchValue(proposal.riskLocations[i]);
        if (proposal.riskLocations[i].skenBuildingValue14 != null) {
          this.showSkenDataForm[i] = true;
          this.updateValidators(i, true);
        }
        if (i + 1 < proposal.riskLocations.length) {
          this.addForm();
        }
      }
      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.RISKLOCATION)
        );
      }
    }
  }

  private isAnyLocationComplete(): boolean {
    return this.formGroup.controls.riskLocations.controls.some(form => {
      const v = form.value;
      return !!(v.description && v.street && v.houseNumber && v.postalCode && v.location && v.countryCode);
    });
  }

  addForm() {
    const form = this.fb.group<RiskLocationForm>({
      riskLocationId: new FormControl<string>(null),
      description: new FormControl<string>(''),
      street: new FormControl<string>(''),
      houseNumber: new FormControl<string>(''),
      postalCode: new FormControl<string>(''),
      location: new FormControl<string>(''),
      countryCode: new FormControl<string>(''),
      geoLocation: new FormControl<string>(''),
      skenBuildingValue14: new FormControl<number>(null),
      skenTotalValueEur: new FormControl<number>(null),
      skenRegionalizedTotalValueM: new FormControl<number>(null),
      skenRegionalizedTotalValueEur: new FormControl<number>(null)
    });

    this.showSkenDataForm.push(false);
    this.formGroup.controls.riskLocations.push(form);

    form.valueChanges.pipe(debounceTime(500),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.destroy$)).subscribe((updatedValues) => {
      const isComplete = !!(
        form.value.street &&
        form.value.houseNumber &&
        form.value.location &&
        form.value.countryCode
      );

      const index = this.formGroup.controls.riskLocations.controls.indexOf(form);
      this.enableSkenDataButton[index] = isComplete;

      this.riskLocationCompleted.emit(this.isAnyLocationComplete());

      if (isComplete) {
        this.riskLocationService
            .getGeoLocationByAddress(form.value.houseNumber + ' ' + form.value.street + ', ' + form.value.location + ', ' + form.value.countryCode)
            .subscribe(gl => {
              if (gl === null) {
                form.patchValue({geoLocation: ''});
              } else {
                form.patchValue({geoLocation: gl.lat + ', ' + gl.lng});
              }
            });
      }
    });

    let search = new RiskLocationSearch();
    search.filteredOptionsCountry.next(this.countryOptions.slice());
    search.filteredOptionsCountryCode.next(this.countryCodeOptions.slice());

    search.filterCtrlCountryCode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCountries(search.filterCtrlCountryCode.value, this.countryCodeOptions, search.filteredOptionsCountryCode);
      });

    search.filterCtrlCountry.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterCountries(search.filterCtrlCountry.value, this.countryOptions, search.filteredOptionsCountry);
      });

    this.riskLocationSearchArray.push(search);
  }

  private updateValidators(index: number, enable: boolean): void {
    const riskLocationForm = this.formGroup.controls.riskLocations.at(index);
    const fields = [
      'skenBuildingValue14',
      'skenTotalValueEur',
      'skenRegionalizedTotalValueM',
      'skenRegionalizedTotalValueEur'
    ];

    fields.forEach((field) => {
      const control = riskLocationForm.get(field);
      if (enable) {
        control.setValidators([Validators.required, CustomValidators.validNumber()]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity(); // Refresh the validation state
    });
  }


  enableShowSkenData(i): void {
    this.userStore.select(selectUser).pipe(take(1)).subscribe((user) => {
      if (user?.skenDataAccountId) {
        // redirect to search mask
        this.showSkenDataForm[i] = true;
        this.updateValidators(i, true); // Enable validators
        this.redirectToSkenDataSearchMask(i);
      } else {
        //open dialog for registration info
        this.openSkenDataRegistrationDialog(i);
      }
    })
  }

  async redirectToSkenDataSearchMask(i:number) {
    await this.skendataService.submitModule2(
      this.formGroup.controls.riskLocations.at(i).value.houseNumber,
      this.formGroup.controls.riskLocations.at(i).value.postalCode,
      this.formGroup.controls.riskLocations.at(i).value.street,
      this.formGroup.controls.riskLocations.at(i).value.location
    );
  }

  openSkenDataRegistrationDialog(i) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {};
    const dialogRef = this.dialog.open(SkendataRegistrationDialogComponent, dialogConfig);
    dialogRef.afterClosed()?.subscribe((result) => {
      if (result) {
        // confirmed registration
        this.redirectToSkenDataSearchMask(i);

        // show skendata input fields
        this.showSkenDataForm[i] = true;
        this.updateValidators(i, true); // Enable validators
      } else {
        // cancelled registration
        this.showSkenDataForm[i] = false;
        this.updateValidators(i, false); // Enable validators
      }
    });

  }

  deleteForm(i): void {
    this.formGroup.controls.riskLocations.removeAt(i);
    this.riskLocationSearchArray.splice(i);
  };

  deleteSkenDataForm(i): void {
    this.formGroup.controls.riskLocations.at(i).patchValue({
      skenBuildingValue14: null,
      skenTotalValueEur: null,
      skenRegionalizedTotalValueM: null,
      skenRegionalizedTotalValueEur: null,
    })
    this.showSkenDataForm[i] = false;
    this.updateValidators(i, false); // Disable validators
  };


  attachFiles(riskLocationFiles: BinaryData[]): void {
    this.formGroup.patchValue({
      riskLocationFiles: riskLocationFiles
    });
  };


  filterCountries(search: string, options, filtered) {
    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(country => country.toLowerCase().indexOf(search) > -1)
    );
  }

  getCountryCodeFromCountry(selectedCountry: string) {
    const index = this.countryOptions.findIndex(country => country === selectedCountry);
    return index !== -1 ? this.countryCodeOptions[index] : '';
  }

  ngAfterViewInit(): void {
    if (!this.proposalPreview) return;

    // if it has risk locations populate fields
    if (this.proposalPreview.riskLocations?.length != null && this.proposalPreview.riskLocations.length > 0) {
      this.handleAttachedFiles();
      this.populateRiskLocations();
    }
  }

  private handleAttachedFiles(): void {
    const attachedFiles = this.proposalPreview.proposalInformation?.attachedFiles;
    if (!attachedFiles) return;

    const riskLocationFiles = attachedFiles.filter(
      file => file.uploadLocation === UploadLocation.RISKLOCATION
    );
    this.uploadComponent.addPreUploadedFiles(riskLocationFiles);
  }

  private populateRiskLocations(): void {
    this.proposalPreview.riskLocations.forEach((location, index) => {
      this.formGroup.controls.riskLocations.at(index).patchValue(location);

      if (this.formGroup.controls.riskLocations.at(index).controls.skenBuildingValue14) {
        this.showSkenDataForm[index] = true;
      }

      if (index + 1 < this.proposalPreview.riskLocations.length) {
        this.addForm();
      }
    });
  }


  reset() {
    this.formGroup.reset();
  }
}

interface RiskLocationForm {
  riskLocationId: FormControl<string>;
  description: FormControl<string>;
  street: FormControl<string>;
  houseNumber: FormControl<string>;
  postalCode: FormControl<string>;
  location: FormControl<string>;
  countryCode: FormControl<string>;
  geoLocation: FormControl<string>;
  skenBuildingValue14: FormControl<number>;
  skenTotalValueEur: FormControl<number>;
  skenRegionalizedTotalValueM: FormControl<number>;
  skenRegionalizedTotalValueEur: FormControl<number>;
}

class RiskLocationSearch {

  public filterCtrlCountry: FormControl<string> = new FormControl<string>('');
  public filterCtrlCountryCode: FormControl<string> = new FormControl<string>('');

  public filteredOptionsCountry: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  public filteredOptionsCountryCode: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

}
